/**
 * RWBR — WHAT THE PAPER IS ALLOWED TO KNOW. D-EZ-50.
 *
 * One object, built out of the fold and nothing else, and it is the ONLY thing
 * the newspaper writer ever sees. If a fact is not in here, no edition of the
 * Dispatch can contain it — which is how D-003's "never invent a result" is
 * enforced structurally rather than asked for politely in a prompt.
 *
 * ===========================================================================
 * WHY IT IS SMALL, AND WHY THAT IS THE HARD PART
 * ===========================================================================
 *
 * A season is tens of thousands of events. A newspaper article is four
 * paragraphs. Handing the model everything would cost a fortune, bury the
 * interesting fact under forty dull ones, and produce a recap rather than a
 * piece of writing — so this module's real job is SELECTION, and every cap in
 * it is a judgement about what a racing weekly would actually print:
 *
 *   • the top five, not the top forty-three;
 *   • the men who fell out, with the reason, capped at five;
 *   • the biggest mover, because "came from 38th to 4th" is the sentence a
 *     reader remembers and it is invisible in a finishing order;
 *   • the championship's top five and who MOVED this week, which is the only
 *     part of a standings table that is news;
 *   • milestones — a first win, a round number of career wins, a drought — which
 *     are the only honest way to close on a career without inventing a mood.
 *
 * ===========================================================================
 * NOTHING HERE IS A RULE
 * ===========================================================================
 *
 * Every number is read off `statsModel.ts`, which reads `standings.ts`'s fold
 * and `money.ts`'s ledger. This module picks and names; it does not count.
 *
 * ⚠ AND NOTHING HERE IS PROSE. No sentence in this file will be printed. The
 * fallback writer composes sentences from these facts and the model writes its
 * own; putting a phrase in here would put the same phrase in both, every week,
 * for ever.
 */

import type { SeasonRaceResult } from '../seasonPlay.ts';
import type { StatsSource } from '../../components/statsModel.ts';
import { allTimeBoard, seasonBoard, type StatBoard, type StatRow } from '../../components/statsModel.ts';
import type { PaperWinter } from './winterFacts.ts';

/**
 * `winter` (D-EZ-102) is the off-season edition: retirements, the Hall of
 * Fame, the awards, records broken and sponsor news, off `winterFacts.ts`.
 */
export type Edition = 'race' | 'season' | 'winter';

export interface PaperDriverLine {
  position: number;
  name: string;
  team: string;
  started: number | null;
}

export interface PaperFacts {
  edition: Edition;
  league: string;
  season: number;
  racesInSeason: number;
  racesRun: number;
  racesRemaining: number;

  race: {
    number: number;
    name: string;
    track: string;
    location: string | null;
    surface: string;
    round: string;
    doublePoints: boolean;
    cautions: number | null;
    leadChanges: number | null;
    winner: {
      name: string;
      team: string;
      started: number | null;
      turnsLed: number;
      ledMost: boolean;
      milesOutFront: number;
      seasonWins: number;
      careerWins: number;
      careerStarts: number;
      firstCareerWin: boolean;
      moneyWonMb: number;
    };
    top5: PaperDriverLine[];
    pole: { name: string; team: string } | null;
    biggestMover: { name: string; team: string; started: number; finished: number; gained: number } | null;
    fellOut: { name: string; team: string; reason: string; running: boolean }[];
    failedToQualify: string[];
    absent: { name: string; reason: string }[];
    /**
     * ⚠ WHAT ACTUALLY HAPPENED OUT THERE — D-EZ-54.
     *
     * Howard, 18 Aug 2026: *"Is it possible for the end of race article to also
     * include some of the happenings and excitement and troubles etc that
     * happened within the particular race as opposed to solely recapping
     * stats?"*
     *
     * It was not, and the reason is the same one that made the booth feel short
     * on ambience: the writer was handed a finishing order, a set of counters
     * and a standings table, and NOTHING about the afternoon. A recap of stats
     * was the only honest thing it could write, because stats were all it had.
     *
     * These are the engine's OWN sentences — every `RuleEvent.description` is
     * written by the rule that fired, names the men involved, and cites the page
     * it came off. So a paragraph built from them cannot invent an incident: the
     * incident is the primary source.
     */
    incidents: PaperIncident[];
    /**
     * D-EZ-71 — WHO TALKED AFTERWARDS, AND ABOUT WHAT.
     *
     * The engine rolls a POST RACE INTERVIEW at the end of roughly half of all
     * races (`postRaceInterview.ts`): the dice choose a category — the winner, a
     * man who wrecked, a man who fumed, a feud — and then a driver inside it.
     * That is a real interview, with a real man, on a subject the ledger knows.
     *
     * ⚠ IT IS THE ONLY GROUNDS ON WHICH THIS PAPER MAY QUOTE ANYBODY. Null on
     * a race where the dice landed in an empty category, and on those races the
     * paper carries no quotes at all — which is the honest report of a Sunday
     * when nobody was put in front of a microphone.
     */
    interview: PaperInterview | null;
  } | null;

  championship: {
    leaderName: string;
    leaderTeam: string;
    leaderPoints: number;
    leaderWins: number;
    marginToSecond: number;
    top5: {
      rank: number;
      name: string;
      team: string;
      points: number;
      behind: number;
      wins: number;
      /** Places gained (+) or lost (−) in this race. Null in week one. */
      moved: number | null;
    }[];
    /** Anyone outside the top five who moved three places or more this week. */
    notableMoves: { name: string; moved: number; rank: number }[];
    round: string;
    inPlayoff: boolean;
    /** Set on the season edition only. */
    championName: string | null;
  };

  seasonSoFar: {
    differentWinners: number;
    mostWins: { name: string; wins: number } | null;
    mostTurnsLed: { name: string; turns: number } | null;
    bestAverageFinish: { name: string; average: number } | null;
    biggestEarner: { name: string; winningsMb: number } | null;
    winlessInTopFive: string[];
  };

  career: {
    seasonsPlayed: number;
    winsLeader: { name: string; wins: number; seasons: number } | null;
    moneyLeader: { name: string; winningsMb: number } | null;
    championships: { name: string; titles: number }[];
    /** True things about somebody's longer arc. The closing paragraph's material. */
    milestones: { name: string; kind: MilestoneKind; value: number }[];
  };

  /**
   * D-EZ-102 / D-EZ-95 — the league's winter news, on a settled season's
   * edition and the winter edition: awards, records broken, retirements, the
   * Hall of Fame, drivers lost, sponsor news. Absent on a race edition.
   */
  winter?: PaperWinter;
}

/** The man the dice put in front of a microphone, and what about. */
export interface PaperInterview {
  driverId: string;
  name: string;
  team: string;
  /** 'winner' | 'wrecked' | 'dnf' | 'fume' | 'feud' | 'top-or-middle'. */
  band: InterviewBand;
  /** The ACTION TAKEN cell, verbatim. Null when the roll was ignored. */
  action: string | null;
  /** Where he finished, when he finished. */
  finished: number | null;
  /** What put him out, when he went out. */
  outReason: string | null;
}

export type InterviewBand = 'winner' | 'wrecked' | 'dnf' | 'fume' | 'feud' | 'top-or-middle';

/** The six the chart has, and nothing else is an interview. */
const BANDS: ReadonlySet<string> = new Set<InterviewBand>([
  'winner',
  'wrecked',
  'dnf',
  'fume',
  'feud',
  'top-or-middle',
]);

/** One thing that happened, in the engine's own words. */
export interface PaperIncident {
  /** The turn it happened on. A turn is ten miles (p.ii). */
  turn: number;
  /** `wreck.big-one`, `duel.win`, `rare.pit-crew-injury`… */
  tag: string;
  /** A plain-English bucket, so the writer can group without parsing tags. */
  kind: IncidentKind;
  /** The rule's own sentence. Never paraphrased on the way here. */
  what: string;
  /** How newsworthy, 0-100. Highest first. */
  weight: number;
}

export type IncidentKind =
  | 'the big one'
  | 'a wreck'
  | 'trouble'
  | 'a duel'
  | 'a feud'
  | 'a mechanical'
  | 'the pits'
  | 'an injury'
  | 'a penalty'
  | 'the weather'
  | 'a charge'
  | 'the finish';

export type MilestoneKind =
  | 'first-career-win'
  | 'career-win-milestone'
  | 'first-win-of-season'
  | 'ended-a-drought'
  | 'first-career-pole'
  | 'career-start-milestone';

// ---------------------------------------------------------------------------
// Trimming a season back to a given week
// ---------------------------------------------------------------------------

/**
 * THE SEASON AS IT STOOD AFTER RACE N.
 *
 * The article for race 14 must not know what happened in race 15, and a player
 * re-reading an old edition is exactly the case where it would. Rather than
 * threading a week number through every reader, the SOURCE is trimmed and the
 * ordinary board is folded over it — so "as of week 14" is a slice, not a mode.
 */
export function through(source: StatsSource, raceNumber: number): StatsSource {
  return {
    ...source,
    races: source.races.filter((r) => r.raceNumber <= raceNumber),
    standingsAfterRace: source.standingsAfterRace.slice(0, raceNumber),
    moneyEvents: source.moneyEvents.filter((e) => e.raceNumber === null || e.raceNumber <= raceNumber),
  };
}

// ---------------------------------------------------------------------------
// Building one edition
// ---------------------------------------------------------------------------

export interface FactsInput {
  leagueName: string;
  /** Every season the shell can reach, oldest first. */
  sources: readonly StatsSource[];
  seasonNumber: number;
  /** Omit for the season edition. */
  raceNumber?: number;
}

const NOTABLE_MOVE = 3;
const CAREER_WIN_MILESTONES = [1, 5, 10, 25, 50, 75, 100, 150, 200];
const CAREER_START_MILESTONES = [100, 200, 300, 400, 500];
const DROUGHT_RACES = 20;

export function paperFacts(input: FactsInput): PaperFacts | null {
  const source = input.sources.find((s) => s.seasonNumber === input.seasonNumber);
  if (!source) return null;

  const isSeasonEdition = input.raceNumber === undefined;
  const raceNumber = input.raceNumber ?? source.standingsAfterRace.length;
  if (raceNumber < 1) return null;

  const trimmed = through(source, raceNumber);
  const board = seasonBoard(trimmed);
  if (board.drivers.length === 0) return null;

  const raceResult = source.races.find((r) => r.raceNumber === raceNumber) ?? null;
  const column = board.races.find((r) => r.raceNumber === raceNumber) ?? null;

  // Career is every season UP TO AND INCLUDING this one, trimmed to this week.
  // A career total that includes next season would be a fact from the future.
  const careerSources = input.sources
    .filter((s) => s.seasonNumber < input.seasonNumber)
    .concat([trimmed]);
  const career = allTimeBoard(careerSources);

  const byId = new Map(board.drivers.map((d) => [d.driverId, d]));
  const careerById = new Map(career.drivers.map((d) => [d.driverId, d]));

  return {
    edition: isSeasonEdition ? 'season' : 'race',
    league: input.leagueName,
    season: input.seasonNumber,
    racesInSeason: board.totalRaces,
    racesRun: raceNumber,
    racesRemaining: Math.max(0, board.totalRaces - raceNumber),

    race:
      raceResult && column
        ? {
            number: raceNumber,
            name: column.raceName,
            track: column.trackName,
            location: column.location,
            surface: column.category,
            round: column.round,
            doublePoints: column.doublePoints,
            cautions: column.cautions,
            leadChanges: column.leadChanges,
            winner: winnerFacts(column.winnerId, byId, careerById, raceNumber),
            top5: topFive(board.drivers, raceNumber),
            pole: column.poleId
              ? {
                  name: byId.get(column.poleId)?.name ?? column.poleName ?? column.poleId,
                  team: byId.get(column.poleId)?.teamName ?? 'independent',
                }
              : null,
            biggestMover: biggestMover(board.drivers, raceNumber),
            fellOut: fellOut(board.drivers, raceNumber),
            failedToQualify: board.drivers
              .filter((d) => d.byRace[raceNumber]?.outcome === 'dnq')
              .slice(0, 4)
              .map((d) => d.name),
            absent: board.drivers
              .filter((d) => d.byRace[raceNumber]?.outcome === 'absent')
              .slice(0, 4)
              .map((d) => ({ name: d.name, reason: d.byRace[raceNumber].note ?? 'not entered' })),
            incidents: raceIncidents(raceResult),
            interview: raceInterview(raceResult, byId),
          }
        : null,

    championship: {
      leaderName: board.drivers[0].name,
      leaderTeam: board.drivers[0].teamName,
      leaderPoints: board.drivers[0].points,
      leaderWins: board.drivers[0].wins,
      marginToSecond: board.drivers[1] ? board.drivers[0].points - board.drivers[1].points : 0,
      top5: board.drivers.slice(0, 5).map((d) => ({
        rank: d.rank,
        name: d.name,
        team: d.teamName,
        points: d.points,
        behind: d.pointsBehind,
        wins: d.wins,
        moved: d.rankDelta,
      })),
      notableMoves: board.drivers
        .slice(5, 30)
        .filter((d) => d.rankDelta !== null && Math.abs(d.rankDelta) >= NOTABLE_MOVE)
        .slice(0, 4)
        .map((d) => ({ name: d.name, moved: d.rankDelta as number, rank: d.rank })),
      round: column?.round ?? 'regular',
      inPlayoff: (column?.round ?? 'regular') !== 'regular',
      championName: isSeasonEdition && source.championId
        ? (byId.get(source.championId)?.name ?? null)
        : null,
    },

    seasonSoFar: {
      differentWinners: new Set(
        board.races.filter((r) => r.run && r.winnerId).map((r) => r.winnerId as string),
      ).size,
      mostWins: pick(board.drivers, (d) => d.wins, (d) => ({ name: d.name, wins: d.wins })),
      mostTurnsLed: pick(board.drivers, (d) => d.turnsLed, (d) => ({ name: d.name, turns: d.turnsLed })),
      bestAverageFinish: pick(
        board.drivers.filter((d) => d.starts >= Math.max(1, Math.floor(raceNumber / 2))),
        (d) => (d.avgFinish === null ? null : -d.avgFinish),
        (d) => ({ name: d.name, average: Number((d.avgFinish as number).toFixed(1)) }),
      ),
      biggestEarner: pick(
        board.drivers,
        (d) => d.winningsMb,
        (d) => ({ name: d.name, winningsMb: Number(d.winningsMb.toFixed(3)) }),
      ),
      // "Second in points and has not won a race" is a fact a racing weekly
      // prints every year, and it is invisible in any single column.
      winlessInTopFive: board.drivers.slice(0, 5).filter((d) => d.wins === 0).map((d) => d.name),
    },

    career: {
      seasonsPlayed: careerSources.length,
      winsLeader: pick(
        career.drivers,
        (d) => d.wins,
        (d) => ({ name: d.name, wins: d.wins, seasons: d.seasons }),
      ),
      moneyLeader: pick(
        career.drivers,
        (d) => d.winningsMb,
        (d) => ({ name: d.name, winningsMb: Number(d.winningsMb.toFixed(3)) }),
      ),
      championships: career.drivers
        .filter((d) => d.championships > 0)
        .slice(0, 5)
        .map((d) => ({ name: d.name, titles: d.championships })),
      milestones: milestones(board, career, careerById, raceNumber),
    },
  };
}

// ---------------------------------------------------------------------------
// The afternoon itself
// ---------------------------------------------------------------------------

/**
 * ⚠ HOW NEWSWORTHY EACH KIND OF EVENT IS — the editorial judgement in this file.
 *
 * A race emits thousands of events. Almost all of them are the machinery of the
 * game working — a challenge that changed nothing, a chip held, a gap filled —
 * and a newspaper prints none of it. What a newspaper prints is the twelve
 * things that a person at the track would still be talking about on the way
 * home, and this table is what says which twelve.
 *
 * The weights are not fine-grained on purpose. The Big One outranks a wreck
 * outranks a spin; an injury outranks a mechanical; a duel for the lead in the
 * last third outranks the same duel on turn four (`weightOf` handles that part).
 * Anything not in this table is machinery and is dropped — including every
 * ordinary challenge, which is what `challenge.sequence` mostly is.
 */
const INCIDENT_WEIGHTS: Record<string, { kind: IncidentKind; weight: number }> = {
  'wreck.big-one': { kind: 'the big one', weight: 100 },
  'rare.wreck-and-fight': { kind: 'a wreck', weight: 92 },
  'rare.out-of-race': { kind: 'a wreck', weight: 88 },
  'wreck.big-trouble': { kind: 'a wreck', weight: 84 },
  'trouble.multi-car': { kind: 'a wreck', weight: 82 },
  'injury.serious': { kind: 'an injury', weight: 95 },
  'injury.moderate': { kind: 'an injury', weight: 78 },
  'injury.minor': { kind: 'an injury', weight: 58 },
  'rare.pit-crew-injury': { kind: 'the pits', weight: 86 },
  'rare.pit-crew-incident': { kind: 'the pits', weight: 70 },
  'rare.pit-error': { kind: 'the pits', weight: 66 },
  'rare.rain-threat': { kind: 'the weather', weight: 74 },
  'rare.surge': { kind: 'a charge', weight: 68 },
  'feud.wreck': { kind: 'a feud', weight: 90 },
  'feud.demote': { kind: 'a feud', weight: 60 },
  'wreck.trouble': { kind: 'trouble', weight: 62 },
  'trouble.collected': { kind: 'trouble', weight: 56 },
  'trouble.escaped': { kind: 'trouble', weight: 34 },
  'repair.failed': { kind: 'a mechanical', weight: 64 },
  'repair.impossible': { kind: 'a mechanical', weight: 66 },
  'duel.win': { kind: 'a duel', weight: 55 },
  'duel.loss': { kind: 'a duel', weight: 52 },
  'duel.declined': { kind: 'a duel', weight: 30 },
  'restart.turn-cancelled': { kind: 'the finish', weight: 60 },
  'turn.final-black-five': { kind: 'the finish', weight: 72 },
  'yellow.lucky-dog': { kind: 'trouble', weight: 40 },
  'pfa.won-the-race': { kind: 'the finish', weight: 96 },
  'pfa.passed': { kind: 'the finish', weight: 76 },
  'pfa.defended': { kind: 'the finish', weight: 74 },
};

/** How many make the paper. Twelve is a long article; the rest is machinery. */
const MAX_INCIDENTS = 12;
/** No more than this many of any one kind. See the note in `raceIncidents`. */
const MAX_PER_KIND = 3;
/** The engine writes some long sentences. A quoted fact does not need all of it. */
const MAX_INCIDENT_CHARS = 260;

/**
 * THE TWELVE THINGS WORTH PRINTING, best first.
 *
 * Late drama outranks early drama at the same weight — a wreck on the last turn
 * is the story of the race and the same wreck on turn three is something that
 * happened before the story started. That is a real editorial rule and it is why
 * the turn is part of the score rather than only part of the output.
 */
/**
 * The post-race interview, read out of the season slice of the ledger.
 *
 * ⚠ `race.events` AND NOT `race.race.events`. The interview is a SEASON event —
 * it happens after the checkered flag, it is filed with `turn: null`, and it is
 * therefore in the slice the season stream holds rather than in the race's own
 * turn-by-turn. Reading the wrong one finds nothing, silently, for ever. (It is
 * also why the archive keeps it: D-EZ-65 strips events with a turn and keeps the
 * ones without.)
 */
export function raceInterview(
  race: SeasonRaceResult | null,
  byId: Map<string, StatRow>,
): PaperInterview | null {
  if (!race) return null;
  for (const event of race.events ?? []) {
    if (event.eventType !== 'post-race-interview') continue;
    // "Nobody qualifies in that category tonight" — a real roll and no
    // interview. There is nobody to quote and the paper says nothing.
    if (event.momentTag === 'interview.ignored') continue;
    /*
      ⚠ AND `interview.applied` IS NOT AN INTERVIEW EITHER.

      `seasonPlay` files TWO events under this type: the ROLL, tagged with the
      category the dice landed in, and — a week later, on the race where the
      effect is actually used — an `interview.applied`. Both name a driver, both
      carry an "action taken", and taking the first match found fourteen of
      seventeen "interviews" that were really last week's paperwork. The paper
      would have quoted a man about a microphone he never stood in front of.
    */
    const band = event.momentTag.replace(/^interview\./, '');
    if (!BANDS.has(band)) continue;
    const driverId = event.actorIds[0];
    if (!driverId) continue;
    const row = byId.get(driverId);
    const action = event.modifiers.find((m) => m.name === 'action taken')?.value;
    const finish = race.race.finishingOrder.find((f) => f.driverId === driverId);

    return {
      driverId,
      name: row?.name ?? String(event.modifiers.find((m) => m.name === 'driver')?.value ?? driverId),
      team: row?.teamName ?? '',
      band: band as InterviewBand,
      action: typeof action === 'string' ? action : null,
      finished: finish && finish.status === 'running' ? finish.place : null,
      outReason: finish && finish.status === 'out' ? (finish.outReason ?? null) : null,
    };
  }
  return null;
}

export function raceIncidents(race: SeasonRaceResult | null): PaperIncident[] {
  if (!race) return [];
  const total = race.race.turnsRun || race.race.scheduledTurns || 1;

  const scored: PaperIncident[] = [];
  for (const event of race.race.events) {
    const entry = INCIDENT_WEIGHTS[event.momentTag];
    if (!entry) continue;
    const text = (event.description ?? '').trim();
    if (text.length < 12) continue;
    scored.push({
      turn: event.turn,
      tag: event.momentTag,
      kind: entry.kind,
      what: text.length > MAX_INCIDENT_CHARS ? `${text.slice(0, MAX_INCIDENT_CHARS - 1).trimEnd()}…` : text,
      // Up to +12 for happening late. Enough to lift a last-lap duel over an
      // opening-lap one; never enough to lift a duel over the Big One.
      weight: Math.round(entry.weight + 12 * (event.turn / total)),
    });
  }

  scored.sort((a, b) => b.weight - a.weight || a.turn - b.turn);

  /*
    ONE PER KIND BEFORE A SECOND OF ANY — otherwise a race with nine spins
    prints nine spins and no mention of the wreck that decided it. A newspaper
    covers the afternoon, not the most common thing in it.
  */
  const count = new Map<IncidentKind, number>();
  const first: PaperIncident[] = [];
  const rest: PaperIncident[] = [];
  for (const incident of scored) {
    const at = count.get(incident.kind) ?? 0;
    count.set(incident.kind, at + 1);
    if (at === 0) first.push(incident);
    // ⚠ AND NO MORE THAN THREE OF ANY ONE KIND. Measured on a real season: a
    // race can throw a dozen duels and nothing else notable, and without this
    // the list came back as twelve duels — a true list, and a useless one for
    // anybody trying to describe an afternoon. Beyond three, the fourth duel
    // tells a reader nothing the third did not.
    else if (at < MAX_PER_KIND) rest.push(incident);
  }
  return [...first, ...rest].slice(0, MAX_INCIDENTS).sort((a, b) => a.turn - b.turn);
}

// ---------------------------------------------------------------------------

function pick<R>(
  rows: readonly StatRow[],
  score: (d: StatRow) => number | null,
  shape: (d: StatRow) => R,
): R | null {
  let best: StatRow | null = null;
  let high = -Infinity;
  for (const d of rows) {
    const v = score(d);
    if (v === null) continue;
    if (v > high) {
      high = v;
      best = d;
    }
  }
  // A leader on zero is not a leader — "most wins: nobody, 0" is not a fact
  // worth printing and it is exactly the kind the model would turn into one.
  if (!best || high <= 0) return null;
  return shape(best);
}

function winnerFacts(
  winnerId: string | null,
  byId: Map<string, StatRow>,
  careerById: Map<string, StatRow>,
  raceNumber: number,
): PaperFacts['race'] extends null ? never : NonNullable<PaperFacts['race']>['winner'] {
  const row = winnerId ? byId.get(winnerId) : undefined;
  const careerRow = winnerId ? careerById.get(winnerId) : undefined;
  const line = row?.byRace[raceNumber];
  const careerWins = careerRow?.wins ?? row?.wins ?? 0;
  return {
    name: row?.name ?? 'unknown',
    team: row?.teamName ?? 'independent',
    started: line?.start ?? null,
    turnsLed: line?.turnsLed ?? 0,
    ledMost: Boolean(line?.ledMost),
    // A turn is ten miles (p.ii), not a lap. Converted here so the writer is
    // never handed a unit it has to reason about.
    milesOutFront: (line?.turnsLed ?? 0) * 10,
    seasonWins: row?.wins ?? 0,
    careerWins,
    careerStarts: careerRow?.starts ?? row?.starts ?? 0,
    firstCareerWin: careerWins === 1,
    moneyWonMb: Number((line?.winningsMb ?? 0).toFixed(3)),
  };
}

function topFive(rows: readonly StatRow[], raceNumber: number): PaperDriverLine[] {
  const out: PaperDriverLine[] = [];
  for (const d of rows) {
    const line = d.byRace[raceNumber];
    if (!line || line.outcome !== 'ran' || line.finish === null) continue;
    if (line.finish > 5) continue;
    out.push({ position: line.finish, name: d.name, team: d.teamName, started: line.start });
  }
  return out.sort((a, b) => a.position - b.position);
}

/** Started deep, finished high. Ignores anyone who started inside the top ten. */
function biggestMover(rows: readonly StatRow[], raceNumber: number) {
  let best: { name: string; team: string; started: number; finished: number; gained: number } | null = null;
  for (const d of rows) {
    const line = d.byRace[raceNumber];
    if (!line || line.outcome !== 'ran' || line.finish === null || line.start === null) continue;
    if (!line.running) continue;
    const gained = line.start - line.finish;
    if (gained < 10 || line.start <= 10) continue;
    if (!best || gained > best.gained) {
      best = { name: d.name, team: d.teamName, started: line.start, finished: line.finish, gained };
    }
  }
  return best;
}

function fellOut(rows: readonly StatRow[], raceNumber: number) {
  return rows
    .map((d) => ({ d, line: d.byRace[raceNumber] }))
    .filter(({ line }) => line && line.outcome === 'ran' && !line.running)
    .slice(0, 5)
    .map(({ d, line }) => ({
      name: d.name,
      team: d.teamName,
      reason: line.outReason ?? 'unknown',
      running: false,
    }));
}

/**
 * TRUE THINGS ABOUT A LONGER ARC — the closing paragraph's raw material.
 *
 * Every one is checkable against the fold, and every one is about the race that
 * has just happened. A milestone list that reached for "he has never won here"
 * would need a track history the fold does not keep, so it is not in here: the
 * paper closes on nothing rather than on something almost true.
 */
function milestones(
  board: StatBoard,
  career: StatBoard,
  careerById: Map<string, StatRow>,
  raceNumber: number,
): PaperFacts['career']['milestones'] {
  const out: PaperFacts['career']['milestones'] = [];
  const column = board.races.find((r) => r.raceNumber === raceNumber);
  const winnerId = column?.winnerId ?? null;

  if (winnerId) {
    const seasonRow = board.drivers.find((d) => d.driverId === winnerId);
    const careerRow = careerById.get(winnerId);
    const careerWins = careerRow?.wins ?? seasonRow?.wins ?? 0;

    if (careerWins === 1) {
      out.push({ name: seasonRow?.name ?? '', kind: 'first-career-win', value: careerRow?.starts ?? 0 });
    } else if (CAREER_WIN_MILESTONES.includes(careerWins)) {
      out.push({ name: seasonRow?.name ?? '', kind: 'career-win-milestone', value: careerWins });
    }
    if (seasonRow && seasonRow.wins === 1) {
      out.push({ name: seasonRow.name, kind: 'first-win-of-season', value: raceNumber });
    }
    if (careerRow && CAREER_START_MILESTONES.includes(careerRow.starts)) {
      out.push({ name: careerRow.name, kind: 'career-start-milestone', value: careerRow.starts });
    }
  }

  // A drought is measured in races entered, not weeks on a calendar: a man who
  // sat out four of them has not been losing for four of them.
  for (const d of board.drivers.slice(0, 12)) {
    if (d.wins === 0) continue;
    if (d.lastWinRace === null || d.lastWinRace === raceNumber) continue;
    const since = raceNumber - d.lastWinRace;
    if (since >= DROUGHT_RACES) out.push({ name: d.name, kind: 'ended-a-drought', value: since });
  }

  void career;
  return out.slice(0, 4);
}
